/**
 * Budget types for Monumenta Financial Settings
 *
 * Re-exports shared types from backend for frontend use.
 * This ensures type consistency between frontend and backend.
 *
 * Source: packages/serverless/api/src/handlers/monumenta/budgets/shared/types.ts
 *
 * @module types/budgets
 * @description TypeScript type definitions for budget-related data structures.
 * These types match the backend API contract exactly to ensure proper serialization/deserialization.
 *
 * @example
 * import { ClientBudgetSettings, formatCurrency, isValidYear } from '@/types/budgets'
 *
 * const budget: ClientBudgetSettings = {
 *   clientCode: '000054',
 *   clientName: 'SEBRAE',
 *   year: 2025,
 *   settings: { targetRevenue: 16000000, labourCost: 8000000 },
 *   metadata: { lastUpdated: '2025-10-21T14:30:00.000Z', updatedBy: 'director@monumenta.com' }
 * }
 *
 * console.log(formatCurrency(budget.settings.targetRevenue)) // "R$ 16.000.000,00"
 */

// NOTE: In a production environment, these types would be imported from a shared package.
// For now, we duplicate the essential types needed by the frontend.

// ============================================================================
// API Request/Response Types
// ============================================================================

/**
 * Budget settings for a client or company projection
 *
 * @description Represents the six key financial metrics that can be manually configured
 * for each client on a per-year basis. All fields are nullable to handle partial data
 * and gracefully represent missing values.
 *
 * @property {number | null} targetRevenue - PROA target: Projected revenue for the year
 * @property {number | null} targetCosts - Projected operational costs for the year
 * @property {number | null} targetTax - Projected tax obligations for the year
 * @property {number | null} targetMargin - Projected profit margin for the year
 * @property {number | null} labourCost - Folha de pagamento: Labour allocation costs per client
 * @property {number | null} approvedWork - Encarteirado: Approved work amount per client
 */
export interface BudgetSettings {
  targetRevenue?: number | null
  targetCosts?: number | null
  targetTax?: number | null
  targetMargin?: number | null
  labourCost?: number | null
  approvedWork?: number | null
}

/**
 * Budget metadata for audit tracking
 *
 * @description Tracks when a budget was last modified and by whom.
 * Both fields are nullable since new records won't have modification history.
 * These fields are managed by the backend and should be treated as read-only.
 *
 * @property {string | null} lastUpdated - ISO timestamp of last modification (e.g., "2025-10-21T14:30:00.000Z")
 * @property {string | null} updatedBy - Email address of user who made the last update
 */
export interface BudgetMetadata {
  readonly lastUpdated: string | null
  readonly updatedBy: string | null
}

/**
 * Client budget settings response
 *
 * @description Complete budget information for a specific client and year.
 * Returned by GET /budgets/client/:clientCode?year=YYYY endpoint.
 *
 * @property {string} clientCode - Unique client identifier (e.g., "000054")
 * @property {string} clientName - Client display name (e.g., "SEBRAE")
 * @property {number} year - Budget year (must be between 2020-2099)
 * @property {BudgetSettings} settings - Financial settings for this client-year
 * @property {BudgetMetadata} metadata - Audit information (read-only)
 *
 * @example
 * const clientBudget: ClientBudgetSettings = {
 *   clientCode: '000054',
 *   clientName: 'SEBRAE',
 *   year: 2025,
 *   settings: {
 *     targetRevenue: 16000000,
 *     targetCosts: 12000000,
 *     labourCost: 8000000
 *   },
 *   metadata: {
 *     lastUpdated: '2025-10-21T14:30:00.000Z',
 *     updatedBy: 'director@monumenta.com'
 *   }
 * }
 */
export interface ClientBudgetSettings {
  clientCode: string
  clientName: string
  year: number
  settings: BudgetSettings
  metadata: BudgetMetadata
}

/**
 * Request body for updating client budget
 *
 * @description Used when making PUT /budgets/client/:clientCode/:year requests.
 * Supports partial updates - only provide fields that should be changed.
 *
 * @property {Partial<BudgetSettings>} settings - Financial settings to update (partial object)
 *
 * @example
 * const updateRequest: UpdateClientBudgetRequest = {
 *   settings: {
 *     targetRevenue: 18000000,  // Only updating revenue
 *     labourCost: 9000000       // and labour cost
 *   }
 * }
 */
export interface UpdateClientBudgetRequest {
  settings: Partial<BudgetSettings>
}

/**
 * Response for listing all client budgets for a year
 *
 * @description Returned by GET /budgets/clients?year=YYYY endpoint.
 * Contains all clients with budget data for the specified year.
 *
 * @property {number} year - Budget year requested
 * @property {ClientBudgetSettings[]} clients - Array of client budget settings
 * @property {number} totalClients - Count of clients with budget data
 */
export interface ClientBudgetsList {
  year: number
  clients: ClientBudgetSettings[]
  totalClients: number
}

/**
 * Company-wide training budget information
 *
 * @description Training budget and actual costs for a specific year.
 * Returned by GET /budgets/training?year=YYYY endpoint.
 * Variance and utilization are calculated automatically by the backend.
 *
 * @property {number} year - Budget year
 * @property {number} budget - Planned training budget amount
 * @property {number} actualCost - Actual training expenditure to date
 * @property {number} variance - Difference between actual and budget (actualCost - budget)
 * @property {number} utilizationPercent - Percentage of budget utilized ((actualCost / budget) * 100)
 * @property {BudgetMetadata} metadata - Audit information (read-only)
 *
 * @example
 * const training: TrainingBudget = {
 *   year: 2025,
 *   budget: 500000,
 *   actualCost: 287500,
 *   variance: -212500,           // Under budget (negative is good)
 *   utilizationPercent: 57.5,    // 57.5% utilized
 *   metadata: {
 *     lastUpdated: '2025-10-21T10:00:00.000Z',
 *     updatedBy: 'admin@monumenta.com'
 *   }
 * }
 */
export interface TrainingBudget {
  year: number
  budget: number
  actualCost: number
  variance: number
  utilizationPercent: number
  metadata: BudgetMetadata
}

/**
 * Request body for updating training budget
 *
 * @description Used when making PUT /budgets/training/:year requests.
 * Both fields are optional to support partial updates.
 *
 * @property {number} budget - Planned training budget (optional)
 * @property {number} actualCost - Actual training expenditure (optional)
 */
export interface UpdateTrainingBudgetRequest {
  budget?: number
  actualCost?: number
}

/**
 * Company-wide budget projections summary
 *
 * @description Aggregated totals across all clients for a specific year.
 * Returned by GET /budgets/projections?year=YYYY endpoint.
 *
 * @property {number} year - Budget year
 * @property {object} projections - Aggregated financial projections
 * @property {number} projections.totalRevenue - Sum of all client target revenues
 * @property {number} projections.totalCosts - Sum of all client target costs
 * @property {number} projections.totalTax - Sum of all client target taxes
 * @property {number} projections.totalMargin - Sum of all client target margins
 * @property {number} projections.totalLabourCost - Sum of all client labour costs
 * @property {number} projections.totalApprovedWork - Sum of all client approved work amounts
 * @property {number} clientCount - Number of clients included in aggregation
 * @property {string} lastUpdated - ISO timestamp of most recent client budget update
 *
 * @example
 * const projections: ProjectionsSummary = {
 *   year: 2025,
 *   projections: {
 *     totalRevenue: 45000000,
 *     totalCosts: 32000000,
 *     totalTax: 4500000,
 *     totalMargin: 8500000,
 *     totalLabourCost: 25000000,
 *     totalApprovedWork: 42000000
 *   },
 *   clientCount: 15,
 *   lastUpdated: '2025-10-21T14:00:00.000Z'
 * }
 */
export interface ProjectionsSummary {
  year: number
  projections: {
    totalRevenue: number
    totalCosts: number
    totalTax: number
    totalMargin: number
    totalLabourCost: number
    totalApprovedWork: number
  }
  clientCount: number
  lastUpdated: string
}

// ============================================================================
// Error Types
// ============================================================================

/**
 * Standard API error response format
 *
 * @description All API errors follow this standardized structure.
 * The error object contains all relevant information for debugging and user feedback.
 *
 * @property {object} error - Error container object
 * @property {ErrorCode} error.code - Machine-readable error code
 * @property {string} error.message - Human-readable error message
 * @property {Record<string, any>} error.details - Additional error context (optional)
 * @property {string} error.timestamp - ISO timestamp when error occurred
 * @property {string} error.requestId - Unique request identifier for support/debugging
 *
 * @example
 * const apiError: ApiError = {
 *   error: {
 *     code: 'VALIDATION_ERROR',
 *     message: 'Invalid budget data provided',
 *     details: { field: 'targetRevenue', issue: 'Must be a positive number' },
 *     timestamp: '2025-10-21T15:00:00.000Z',
 *     requestId: 'abc-123-def'
 *   }
 * }
 */
export interface ApiError {
  error: {
    code: ErrorCode
    message: string
    details?: Record<string, any>
    timestamp: string
    requestId: string
  }
}

/**
 * API error codes
 *
 * @description Union type of all possible error codes returned by the API.
 * Use this for exhaustive switch statements in error handling.
 *
 * - UNAUTHORIZED (401): Missing or invalid authentication token
 * - FORBIDDEN (403): User lacks required permissions (not a director)
 * - NOT_FOUND (404): Requested resource does not exist
 * - VALIDATION_ERROR (400): Request data failed validation
 * - CONFLICT (409): Resource was modified by another user
 * - INTERNAL_ERROR (500): Server-side error occurred
 */
export type ErrorCode =
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION_ERROR'
  | 'CONFLICT'
  | 'INTERNAL_ERROR'

/**
 * Form validation error
 *
 * @description Client-side validation errors for form fields.
 * Used by form validation libraries (e.g., Zod, React Hook Form).
 *
 * @property {string} field - Field name that failed validation
 * @property {string} message - Human-readable validation error message
 * @property {string} code - Machine-readable validation error code
 *
 * @example
 * const validationError: ValidationError = {
 *   field: 'targetRevenue',
 *   message: 'Must be greater than or equal to 0',
 *   code: 'min_value'
 * }
 */
export interface ValidationError {
  field: string
  message: string
  code: string
}

// ============================================================================
// Frontend-Specific Types
// ============================================================================

/**
 * Budget form data structure
 *
 * @description Type for form state in budget editing components.
 * All fields are nullable and optional to support partial data entry and empty states.
 *
 * @property {number | null | undefined} targetRevenue - PROA target revenue
 * @property {number | null | undefined} targetCosts - Projected costs
 * @property {number | null | undefined} targetTax - Projected tax
 * @property {number | null | undefined} targetMargin - Projected margin
 * @property {number | null | undefined} labourCost - Labour allocation costs
 * @property {number | null | undefined} approvedWork - Approved work amount
 */
export interface BudgetFormData {
  targetRevenue: number | null | undefined
  targetCosts: number | null | undefined
  targetTax: number | null | undefined
  targetMargin: number | null | undefined
  labourCost: number | null | undefined
  approvedWork: number | null | undefined
}

/**
 * Client budget with UI state
 *
 * @description Extends ClientBudgetSettings with UI state flags for managing
 * edit mode, loading states, and unsaved changes in React components.
 *
 * @property {boolean} isEditing - Whether the budget is currently being edited
 * @property {boolean} isSaving - Whether a save operation is in progress
 * @property {boolean} hasUnsavedChanges - Whether there are unsaved modifications
 */
export interface ClientBudgetWithState extends ClientBudgetSettings {
  isEditing: boolean
  isSaving: boolean
  hasUnsavedChanges: boolean
}

/**
 * Training budget with UI state
 *
 * @description Extends TrainingBudget with UI state flags for managing
 * edit mode, loading states, and unsaved changes in React components.
 *
 * @property {boolean} isEditing - Whether the budget is currently being edited
 * @property {boolean} isSaving - Whether a save operation is in progress
 * @property {boolean} hasUnsavedChanges - Whether there are unsaved modifications
 */
export interface TrainingBudgetWithState extends TrainingBudget {
  isEditing: boolean
  isSaving: boolean
  hasUnsavedChanges: boolean
}

// ============================================================================
// Constants
// ============================================================================

export const ERROR_CODES = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  CONFLICT: 'CONFLICT',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format a number as Brazilian currency (R$)
 *
 * @description Formats monetary values using Brazilian locale (pt-BR) with proper
 * thousands separators and decimal places. Handles null values gracefully by
 * returning a placeholder string.
 *
 * @param {number | null} value - The monetary value to format
 * @param {string} locale - Locale for formatting (default: 'pt-BR')
 * @returns {string} Formatted currency string (e.g., "R$ 16.000.000,00") or "—" for null
 *
 * @example
 * formatCurrency(16000000)        // "R$ 16.000.000,00"
 * formatCurrency(0)               // "R$ 0,00"
 * formatCurrency(null)            // "—"
 * formatCurrency(1234.56)         // "R$ 1.234,56"
 */
export function formatCurrency(value: number | null | undefined, locale: string = 'pt-BR'): string {
  if (value === null || value === undefined) {
    return '—'
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Format a number as a percentage
 *
 * @description Formats a decimal or whole number as a percentage with specified
 * decimal places. Handles null values gracefully by returning a placeholder string.
 *
 * @param {number | null} value - The value to format (e.g., 0.575 or 57.5)
 * @param {number} decimals - Number of decimal places to show (default: 1)
 * @returns {string} Formatted percentage string (e.g., "57.5%") or "—" for null
 *
 * @example
 * formatPercent(57.5, 1)          // "57.5%"
 * formatPercent(0.575, 2)         // "0.58%"
 * formatPercent(null)             // "—"
 * formatPercent(100, 0)           // "100%"
 */
export function formatPercent(value: number | null, decimals: number = 1): string {
  if (value === null || value === undefined) {
    return '—'
  }

  return `${value.toFixed(decimals)}%`
}

/**
 * Calculate variance between actual and budgeted amounts
 *
 * @description Calculates the difference between actual and budget values.
 * Negative variance indicates under budget (good), positive indicates over budget (bad).
 *
 * @param {number} actual - The actual amount spent or earned
 * @param {number} target - The budgeted/target amount
 * @returns {number} Variance (actual - target)
 *
 * @example
 * calculateVariance(287500, 500000)   // -212500 (under budget)
 * calculateVariance(600000, 500000)   //  100000 (over budget)
 * calculateVariance(500000, 500000)   //       0 (on budget)
 */
export function calculateVariance(actual: number, target: number): number {
  return actual - target
}

/**
 * Calculate budget utilization percentage
 *
 * @description Calculates what percentage of the budget has been utilized.
 * Returns 0 if budget is 0 to avoid division by zero.
 *
 * @param {number} actual - The actual amount spent or earned
 * @param {number} budget - The budgeted amount
 * @returns {number} Utilization percentage (0-100+)
 *
 * @example
 * calculateUtilizationPercent(287500, 500000)  // 57.5
 * calculateUtilizationPercent(600000, 500000)  // 120.0 (over budget)
 * calculateUtilizationPercent(0, 500000)       // 0
 * calculateUtilizationPercent(100, 0)          // 0 (avoid division by zero)
 */
export function calculateUtilizationPercent(actual: number, budget: number): number {
  if (budget === 0) return 0
  return (actual / budget) * 100
}

/**
 * Validate that a year is within acceptable range
 *
 * @description Checks if a year value is a valid integer between 2020 and 2099.
 * This range aligns with the budget system's supported years.
 *
 * @param {number} year - The year to validate
 * @returns {boolean} True if year is valid, false otherwise
 *
 * @example
 * isValidYear(2025)    // true
 * isValidYear(2020)    // true (boundary)
 * isValidYear(2099)    // true (boundary)
 * isValidYear(2019)    // false (too early)
 * isValidYear(2100)    // false (too late)
 * isValidYear(2025.5)  // false (not an integer)
 */
export function isValidYear(year: number): boolean {
  return Number.isInteger(year) && year >= 2020 && year <= 2099
}

// ============================================================================
// Type Guards and Validation Helpers
// ============================================================================

/**
 * Type guard for BudgetSettings
 *
 * @description Runtime check to verify an object conforms to BudgetSettings interface.
 * Useful for validating API responses or user input.
 *
 * @param {unknown} obj - Object to check
 * @returns {boolean} True if object is valid BudgetSettings
 *
 * @example
 * const data: unknown = await fetchData()
 * if (isBudgetSettings(data)) {
 *   console.log(data.targetRevenue) // TypeScript knows data is BudgetSettings
 * }
 */
export function isBudgetSettings(obj: unknown): obj is BudgetSettings {
  if (typeof obj !== 'object' || obj === null) {
    return false
  }

  const budget = obj as Record<string, unknown>

  // All fields are optional, but if present must be number or null
  const optionalNumberFields: (keyof BudgetSettings)[] = [
    'targetRevenue',
    'targetCosts',
    'targetTax',
    'targetMargin',
    'labourCost',
    'approvedWork',
  ]

  for (const field of optionalNumberFields) {
    if (field in budget) {
      const value = budget[field]
      if (value !== null && value !== undefined && typeof value !== 'number') {
        return false
      }
    }
  }

  return true
}

/**
 * Type guard for ClientBudgetSettings
 *
 * @description Runtime check to verify an object conforms to ClientBudgetSettings interface.
 * Validates all required fields and nested structures.
 *
 * @param {unknown} obj - Object to check
 * @returns {boolean} True if object is valid ClientBudgetSettings
 *
 * @example
 * const response: unknown = await api.get('/budgets/client/000054')
 * if (isClientBudgetSettings(response)) {
 *   console.log(response.clientName) // TypeScript knows response type
 * }
 */
export function isClientBudgetSettings(obj: unknown): obj is ClientBudgetSettings {
  if (typeof obj !== 'object' || obj === null) {
    return false
  }

  const budget = obj as Record<string, unknown>

  // Check required fields
  if (
    typeof budget.clientCode !== 'string' ||
    typeof budget.clientName !== 'string' ||
    typeof budget.year !== 'number'
  ) {
    return false
  }

  // Validate year range
  if (!isValidYear(budget.year)) {
    return false
  }

  // Check settings object
  if (!isBudgetSettings(budget.settings)) {
    return false
  }

  // Check metadata object
  if (typeof budget.metadata !== 'object' || budget.metadata === null) {
    return false
  }

  const metadata = budget.metadata as Record<string, unknown>
  if (
    (metadata.lastUpdated !== null && typeof metadata.lastUpdated !== 'string') ||
    (metadata.updatedBy !== null && typeof metadata.updatedBy !== 'string')
  ) {
    return false
  }

  return true
}

/**
 * Type guard for TrainingBudget
 *
 * @description Runtime check to verify an object conforms to TrainingBudget interface.
 * Validates all required numeric fields and metadata structure.
 *
 * @param {unknown} obj - Object to check
 * @returns {boolean} True if object is valid TrainingBudget
 *
 * @example
 * const data: unknown = await api.get('/budgets/training')
 * if (isTrainingBudget(data)) {
 *   console.log(data.variance) // TypeScript knows data type
 * }
 */
export function isTrainingBudget(obj: unknown): obj is TrainingBudget {
  if (typeof obj !== 'object' || obj === null) {
    return false
  }

  const training = obj as Record<string, unknown>

  // Check required numeric fields
  if (
    typeof training.year !== 'number' ||
    typeof training.budget !== 'number' ||
    typeof training.actualCost !== 'number' ||
    typeof training.variance !== 'number' ||
    typeof training.utilizationPercent !== 'number'
  ) {
    return false
  }

  // Validate year range
  if (!isValidYear(training.year)) {
    return false
  }

  // Check metadata
  if (typeof training.metadata !== 'object' || training.metadata === null) {
    return false
  }

  const metadata = training.metadata as Record<string, unknown>
  if (
    (metadata.lastUpdated !== null && typeof metadata.lastUpdated !== 'string') ||
    (metadata.updatedBy !== null && typeof metadata.updatedBy !== 'string')
  ) {
    return false
  }

  return true
}

/**
 * Safely access nullable budget field
 *
 * @description Utility function for safely accessing budget fields that may be null/undefined.
 * Returns a default value if the field is not set.
 *
 * @param {number | null | undefined} value - The budget field value
 * @param {number} defaultValue - Value to return if field is null/undefined (default: 0)
 * @returns {number} The field value or default
 *
 * @example
 * const budget: BudgetSettings = { targetRevenue: null, labourCost: 8000000 }
 * const revenue = getBudgetValue(budget.targetRevenue, 0)      // 0
 * const labour = getBudgetValue(budget.labourCost, 0)          // 8000000
 */
export function getBudgetValue(
  value: number | null | undefined,
  defaultValue: number = 0
): number {
  return value ?? defaultValue
}
